import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Typography,
  Box,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import axios from "axios";
import { APP_API_V1_BASE } from "../../../config/api";

const INITIAL_FORM_STATE = {
  title: "",
  bumpType: "PATCH",
  versionTag: "",
  deviceTypeId: "",
  description: "",
  url: "",
};

async function uploadFirmwarePackage({
  firmwareFile,
  name,
  deviceTypeId,
  bumpType,
  releaseNotes,
  deviceVersionTag,
}) {
  const token = localStorage.getItem("authToken");
  const activeOrgId = localStorage.getItem("activeOrgId");
  if (!token) {
    throw new Error("AUTH_TOKEN_MISSING");
  }

  const formData = new FormData();
  formData.append("file", firmwareFile);
  formData.append("device_type_id", deviceTypeId);
  formData.append("bump_type", String(bumpType || "PATCH").toUpperCase());

  if (name?.trim()) {
    formData.append("name", name.trim());
  }
  if (releaseNotes?.trim()) {
    formData.append("release_notes", releaseNotes.trim());
  }
  if (deviceVersionTag?.trim()) {
    formData.append("device_version_tag", deviceVersionTag.trim());
  }

  return axios.post(`${APP_API_V1_BASE}/firmware/packages`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...(activeOrgId && { "X-Org-ID": activeOrgId }),
    },
  });
}

export default function OtaAddModel({ open, onClose, onSave }) {
  const fileInputRef = React.useRef(null);
  const [uploadType, setUploadType] = useState("binary");
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const resetDialog = () => {
    setUploadType("binary");
    setFile(null);
    setFormData(INITIAL_FORM_STATE);
    setDeviceTypes([]);
    setLoadingDeviceTypes(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!open) {
      resetDialog();
      return;
    }

    const token = localStorage.getItem("authToken");
    const activeOrgId = localStorage.getItem("activeOrgId");
    if (!token) return;

    setLoadingDeviceTypes(true);
    axios
      .get(`${APP_API_V1_BASE}/device-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrgId && { "X-Org-ID": activeOrgId }),
        },
      })
      .then((res) => {
        setDeviceTypes(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        setDeviceTypes([]);
      })
      .finally(() => setLoadingDeviceTypes(false));
  }, [open]);

  const handleSubmit = async () => {
    if (uploadType !== "binary") {
      alert("External URL upload is not supported for firmware packages yet.");
      return;
    }

    if (!file) {
      alert("Firmware binary is required.");
      return;
    }

    if (!formData.deviceTypeId) {
      alert("Device profile is required.");
      return;
    }

    try {
      await uploadFirmwarePackage({
        firmwareFile: file,
        name: formData.title,
        deviceTypeId: formData.deviceTypeId,
        bumpType: formData.bumpType,
        releaseNotes: formData.description,
        deviceVersionTag: formData.versionTag,
      });

      onSave?.();
      onClose?.();
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "UPLOAD_FAILED";
      alert(`Firmware upload failed: ${errorMessage}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          backgroundColor: "#488de7",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Add Package
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={handleChange("title")}
            />
            <TextField
              select
              label="Version Bump*"
              fullWidth
              value={formData.bumpType}
              onChange={handleChange("bumpType")}
            >
              <MenuItem value="PATCH">PATCH</MenuItem>
              <MenuItem value="MINOR">MINOR</MenuItem>
              <MenuItem value="MAJOR">MAJOR</MenuItem>
            </TextField>
          </Box>

          <TextField
            label="Device Version Tag"
            fullWidth
            value={formData.versionTag}
            onChange={handleChange("versionTag")}
            helperText="Optional device-reported version tag."
          />

          <TextField
            select
            label="Device Profile*"
            fullWidth
            value={formData.deviceTypeId}
            onChange={handleChange("deviceTypeId")}
            disabled={loadingDeviceTypes}
          >
            <MenuItem value="" disabled>
              {loadingDeviceTypes
                ? "Loading device profiles..."
                : "Select device profile"}
            </MenuItem>
            {deviceTypes.map((dt) => (
              <MenuItem key={dt.id} value={dt.id}>
                {dt.name || dt.id}
              </MenuItem>
            ))}
          </TextField>

          <RadioGroup
            row
            value={uploadType}
            onChange={(event) => setUploadType(event.target.value)}
          >
            <FormControlLabel
              value="binary"
              control={<Radio />}
              label="Upload binary file"
            />
            <FormControlLabel
              value="url"
              control={<Radio />}
              label="Use external URL"
              disabled
            />
          </RadioGroup>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="caption">Package File*</Typography>
            <Box
              component="label"
              sx={{
                position: "relative",
                border: "2px dashed #cfd8dc",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f9fafb" },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={(event) => {
                  const selectedFile = event.target.files?.[0];
                  if (selectedFile) {
                    setFile(selectedFile);
                  }
                }}
              />
              {file && (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
              <Typography color="text.secondary">
                {file ? (
                  file.name
                ) : (
                  <>
                    Drag and drop a package file or{" "}
                    <Box
                      component="span"
                      sx={{ color: "#00695c", fontWeight: 600 }}
                    >
                      Browse file
                    </Box>
                  </>
                )}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Allowed file types: .bin, .hex, .img
            </Typography>
          </Box>

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={handleChange("description")}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
